import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Award } from 'lucide-react';
import { skillAdvice, skillLabel, type Skill } from '@ready/shared';
import { useApi } from '@/lib/api';
import { Card, ErrorText } from '@/components/ui';
import { CertificateDownloadButton } from '@/components/certificate-download';
import { ShareQuizCard } from '@/components/quiz/share-quiz';
import { SkillBars } from '@/components/quiz/skill-bars';

/**
 * Results: score, skill profile and advice for weak spots. Questions and
 * answers are never shown afterward, so they can't be collected and shared.
 */
export function AttemptResultPage() {
  const { attemptId = '' } = useParams();
  const api = useApi();
  const attempt = useQuery({
    queryKey: ['attempt', attemptId],
    queryFn: () => api.attempt(attemptId),
    retry: false,
  });

  if (attempt.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (attempt.isError) return <ErrorText error={attempt.error} />;
  const { result, quiz, certificateCode, course, preview } = attempt.data;
  const skills = Object.fromEntries(
    Object.entries(result.skills).map(([k, v]) => [k, v!.percent]),
  ) as Partial<Record<Skill, number>>;

  return (
    <div className="space-y-6">
      {preview && (
        <p className="rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          Author preview: this result isn't recorded anywhere.
        </p>
      )}
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Card>
          <p className="text-sm text-muted-foreground">
            {course ? `${course.title} · final assessment` : quiz.title}
          </p>
          <p className="mt-2 text-4xl font-semibold tabular-nums">{result.percent}%</p>
          <p className="mt-1 text-muted-foreground">
            {result.score} of {result.maxScore} correct ·{' '}
            {result.passed ? 'Passed' : `${quiz.passPercent}% needed to pass`}
          </p>
          {course && !preview && result.passed && (
            <div className="mt-4 rounded-lg bg-primary/10 p-3 text-sm">
              <p className="flex items-center gap-2 font-medium">
                <Award className="size-4 text-primary" aria-hidden /> Course complete
              </p>
              <p className="mt-1 text-muted-foreground">
                {course.certificateCode
                  ? 'Your course certificate is ready.'
                  : 'Congratulations on finishing the course.'}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-4">
                {course.certificateCode && (
                  <CertificateDownloadButton code={course.certificateCode} variant="primary" />
                )}
                <Link to="/certificates" className="inline-flex items-center gap-2 text-primary">
                  Your certificates
                </Link>
              </div>
            </div>
          )}
          {certificateCode && (
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <CertificateDownloadButton code={certificateCode} variant="primary" />
              <Link to="/certificates" className="inline-flex items-center gap-2 text-primary">
                <Award className="size-4" aria-hidden /> Share it on LinkedIn
              </Link>
            </div>
          )}
        </Card>

        <Card title="Your skills">
          <SkillBars skills={skills} />
          {result.focusAreas.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="font-medium">Where to focus</h3>
              {result.focusAreas.map((s) => (
                <div key={s} className="text-sm">
                  <p className="font-medium">{skillLabel(s)}</p>
                  <p className="text-muted-foreground">{skillAdvice(s)}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {quiz.slug && quiz.audience !== 'course' && (
        <ShareQuizCard
          slug={quiz.slug}
          title={quiz.title}
          moment={result.passed ? 'passed' : 'took'}
          audience={quiz.audience}
          heading={
            result.passed ? 'Nicely done! Invite others to try it' : 'Invite others to try it'
          }
          text={
            quiz.audience === 'public'
              ? 'Share this challenge on LinkedIn or with your friends and colleagues, or download posters to run a campaign.'
              : 'Download posters to run a campaign in your organization.'
          }
        />
      )}

      <p className="text-center text-sm text-muted-foreground">
        To keep {quiz.mode === 'assessment' ? 'this assessment' : 'this quiz'} fair for everyone, we
        don't show the questions or answers afterward. Use the tips above, then try again.
      </p>

      <div className="flex justify-center gap-4 text-sm">
        {course ? (
          <Link to={`/c/${course.slug}`} className="text-primary">
            Back to the course
          </Link>
        ) : (
          <Link to="/home" className="text-primary">
            Back to your dashboard
          </Link>
        )}
      </div>
    </div>
  );
}
